import { Grid } from "gridjs";

export type Payload = Record<string, any>;

/** Render an ISO timestamp / date as a human-friendly string for the grids/tables. */
export function formatDate(value: any): string {
    if (!value) return '';
    const d = new Date(value);
    if (isNaN(d.getTime())) return String(value);
    const hasTime = typeof value === 'string' && value.includes('T');
    return d.toLocaleString('en-GB', hasTime
        ? { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }
        : { day: '2-digit', month: 'short', year: 'numeric' });
}

export const config: Payload = {
    loader: "loader",
    grid_ids: {
        details: "grid",
        log: "log-grid",
    },
    grids: {
        log: {
            search: true,
            sort: true,
            // Client-side (in-memory) pagination. No `server` key => Grid.js paginates the data array in the browser.
            pagination: {
                limit: 25,
                summary: true,
            },
            columns: [
                {
                    id: "created",
                    name: 'Created'
                },
                {
                    id: "command",
                    name: 'Command'
                },
            ],
            className: {
                table: 'lisa-table'
            },
            data: []
        },
        details: {
            search: true,
            sort: true,
            // Client-side (in-memory) pagination. No `server` key => Grid.js paginates the data array in the browser.
            pagination: {
                limit: 25,
                summary: true,
            },
            columns: [
                {
                    id: "investorId",
                    name: 'Investor Id'
                },
                {
                    id: "accountId",
                    name: "Account Id"
                },
                {
                    id: "firstName",
                    name: 'First Name'
                },
                {
                    id: "lastName",
                    name: 'Last Name'
                },
                {
                    id: "investorNINO",
                    name: 'NINO'
                },
                {
                    id: "dateOfBirth",
                    name: 'DOB'
                },
                {
                    id: "updated",
                    name: 'Updated'
                },
                {
                    id: "created",
                    name: 'Created'
                },
                {
                    id: "closedDate",
                    name: 'Closed'
                }
            ],
            className: {
                table: 'lisa-table'
            }
        },
    },


    // always POST to backend
    lisa_api: {
        getListOfAllAvailableEndpoints: "post",
        createLisaInvestor: "post",
        createOrTransferLisaAccount: "post",
        reinstateLisaAccount: "post",
        getAccountDetails: "post",
        closeExistingLisaAccount: "post",
        modifyDateOfFirstSubscriptionOfLisaAccount: "post",
        reportDeathOrTerminalIllness: "post",
        sendAnnualReturnOfInformation: "post",
        requestReleaseOfFundsToBuyProperty: "post",
        requestPropertyPurchaseExtension: "post",
        reportOutcomeOfPropertyPurchase: "post",
        viewLifeEvent: "post",
        reportWithdrawalCharge: "post",
        getDetailsOfWithdrawalCharge: "post",
        requestBonusPayment: "post",
        getDetailsOfBonusRequest: "post",
        getPaymentDetailsForBonusClaimOrWithdrawalCharge: "post",
        getListOfAllPaymentsAndDebtsInDateRange: "post"
    },


    map : {
        createOrTransferLisaAccount: {
            transferredFromAccountId: "transferAccount",
            transferredFromLMRN: "transferAccount",
            transferInDate: "transferAccount"
        },
        requestBonusPayment: {
            htbTransferInForPeriod: "htbTransfer",
            htbTransferTotalYTD: "htbTransfer",
            newSubsForPeriod: "inboundPayments",
            newSubsYTD: "inboundPayments",
            totalSubsForPeriod: "inboundPayments",
            totalSubsYTD: "inboundPayments",
            bonusPaidYTD: "bonuses",
            bonusDueForPeriod: "bonuses",
            totalBonusDueYTD: "bonuses",
            claimReason: "bonuses",
            automaticRecoveryAmount: "supersede",
            originalTransactionId: "supersede",
            originalBonusDueForPeriod: "supersede",
            transactionResult: "supersede",
            reason: "supersede"
        },
        reportWithdrawalCharge: {
            originalTransactionId: "supersede",
            originalWithdrawalChargeAmount: "supersede",
            transactionResult: "supersede",
            reason: "supersede"
        },
        reportOutcomeOfPropertyPurchase: {
            originalLifeEventId: "supersede",
            originalEventDate: "supersede"
        },
        requestPropertyPurchaseExtension: {
            originalLifeEventId: "supersede",
            originalEventDate: "supersede"
        },
        requestReleaseOfFundsToBuyProperty: {
            originalLifeEventId: "supersede",
            originalEventDate: "supersede"
        },
        sendAnnualReturnOfInformation: {
            originalLifeEventId: "supersede",
            originalEventDate: "supersede"
        }
    }
}

// Apply the human-friendly date formatter to every date column across all grids.
const DATE_COLUMNS = new Set(['created', 'updated', 'dateOfBirth', 'closedDate']);
for (const gridName of Object.keys(config.grids)) {
    for (const col of (config.grids[gridName].columns ?? [])) {
        if (col && DATE_COLUMNS.has(col.id) && !col.formatter)
            col.formatter = (cell: any) => formatDate(cell);
    }
}
